import { motion } from "framer-motion";
import { ArrowRight, MessageCircle } from "lucide-react";
import { LOGO_URL, CONTACT, STATS } from "@/data/services";
import { TID } from "@/constants/testIds/site";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid={TID.hero.section}
      className="relative overflow-hidden noise pt-32 md:pt-40"
    >
      <div className="hero-grid-bg absolute inset-0 opacity-70 z-0" />

      {/* Glow blobs */}
      <div className="absolute -top-40 -left-40 w-[520px] h-[520px] bg-[#d4af37]/10 blur-[140px] rounded-full z-0" />
      <div className="absolute top-40 -right-40 w-[600px] h-[600px] bg-[#c0c0c0]/5 blur-[160px] rounded-full z-0" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 pb-24 md:pb-32">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Left: text */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="flex items-center gap-3 mb-6"
            >
              <span className="dot-gold" />
              <span className="text-[11px] uppercase tracking-[0.4em] text-white/60">
                Agence digitale · Abidjan · Côte d&apos;Ivoire
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.05 }}
              className="font-display text-[13vw] md:text-[7.5vw] lg:text-[6vw] leading-[0.9] tracking-tighter text-white"
            >
              Boostez votre <br />
              <span className="text-gold-gradient italic">visibilité</span>{" "}
              <span className="text-silver-gradient">en ligne.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.25 }}
              className="mt-8 max-w-xl text-white/70 text-lg md:text-xl leading-relaxed"
            >
              TikTok, Facebook, Instagram, YouTube.{" "}
              <span className="text-white">
                Abonnés, vues, likes, certification, monétisation
              </span>{" "}
              — tout pour propulser votre présence en ligne à prix imbattables.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <a
                href="#contact"
                data-testid={TID.hero.ctaOrder}
                className="btn-gold px-7 py-4 rounded-full inline-flex items-center gap-2 text-[15px]"
              >
                Commander maintenant
                <ArrowRight size={18} />
              </a>
              <a
                href={`https://wa.me/${CONTACT.whatsappRaw}?text=${encodeURIComponent(
                  "Bonjour, je souhaite en savoir plus sur vos packs Digital Création Promo."
                )}`}
                target="_blank"
                rel="noreferrer"
                data-testid={TID.hero.ctaWhatsapp}
                className="btn-ghost px-7 py-4 rounded-full inline-flex items-center gap-2 text-[15px]"
              >
                <MessageCircle size={18} />
                WhatsApp direct
              </a>
            </motion.div>

            {/* Stats strip */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.6 }}
              className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-2xl"
            >
              {STATS.map((s, i) => (
                <div key={s.label} className="border-l border-white/10 pl-4">
                  <div className="font-display text-3xl md:text-4xl text-gold-gradient">
                    {s.value}
                  </div>
                  <div className="text-xs uppercase tracking-widest text-white/50 mt-2">
                    {s.label}
                  </div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right: Logo showcase */}
          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.1, delay: 0.3 }}
              className="relative aspect-square max-w-[520px] mx-auto"
            >
              {/* Frame */}
              <div className="absolute inset-0 gold-ring rounded-3xl overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a]" />
                <img
                  src={LOGO_URL}
                  alt="Logo Digital Création Promo"
                  className="relative z-10 w-full h-full object-cover mix-blend-luminosity opacity-90"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-[#d4af37]/10 via-transparent to-transparent" />
              </div>

              {/* Floating labels */}
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -left-4 top-10 glass px-4 py-3 rounded-xl"
              >
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/50">
                  Certification
                </div>
                <div className="text-white font-medium text-sm mt-1">
                  Dès 12 000 CFA
                </div>
              </motion.div>

              <motion.div
                animate={{ y: [0, 10, 0] }}
                transition={{
                  duration: 4.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 0.5,
                }}
                className="absolute -right-4 bottom-14 glass px-4 py-3 rounded-xl"
              >
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/50">
                  1000 Abonnés TikTok
                </div>
                <div className="text-white font-medium text-sm mt-1">
                  Dès 6 000 CFA
                </div>
              </motion.div>

              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{
                  duration: 5,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 1,
                }}
                className="absolute -bottom-6 left-16 glass px-4 py-3 rounded-xl"
              >
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/50">
                  10 000 Vues
                </div>
                <div className="text-white font-medium text-sm mt-1">
                  2 500 CFA
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Marquee strip */}
        <div className="mt-24 overflow-hidden border-y border-white/10 py-6">
          <div className="flex marquee-track whitespace-nowrap gap-16 items-center">
            {Array.from({ length: 2 }).flatMap((_, k) =>
              [
                "Abonnés TikTok",
                "Vues Facebook",
                "Likes Instagram",
                "Abonnés YouTube",
                "Certification Officielle",
                "Monétisation",
                "Boost Live",
                "Wave · Orange · Moov · MTN",
              ].map((t, i) => (
                <span
                  key={`${k}-${i}`}
                  className="font-display text-4xl md:text-5xl stroke-gold"
                >
                  {t} ✦
                </span>
              ))
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
